import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, Observable, Subject } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AppointmentType, ListData, UserResourceField } from '@pt/pt-models';
import { UsersResourcesService } from '@pt/api-client';
import { takeUntil } from 'rxjs/operators';
import { UserResource } from 'it/primatoo/openapi/api/org/schemas/userresource/UserResource';
import { ColumnType } from 'it/primatoo/openapi/api/org/schemas/common/ColumnType';

@Component({
  selector: 'pt-select-resource',
  template: `
    <div class='modal-header'>
      <h4 class='modal-title'>
        {{ title | async }}</h4>
    </div>

    <div class='modal-body'>
      <pt-select-user-resource-table
        [data]='usersResourceListData'
        [selectedUserResource]='selectedUserResource'
        (userResourceSelected)='onSelectResource($event)'
      ></pt-select-user-resource-table>
    </div>

    <div class='modal-footer'>
      <button type='button' class='btn btn-primary btn-sm'
              [disabled]='formGroup?.invalid'
              (click)='confirm(); $event.stopPropagation()'>
        {{ 'BUTTON.CONFIRM' | translate }}
      </button>
      <button ngbAutofocus type='button' class='btn btn-outline-secondary btn-sm'
              (click)='activeModal.close(false); $event.stopPropagation()'>
        {{ 'BUTTON.CANCEL' | translate }}
      </button>
    </div>
  `,
  styles: [``]
})
export class SelectUserResourceDialogComponent implements OnInit, OnDestroy {
  @Input() title: Observable<string>;
  @Input() selectedUserResourceId: number;
  @Input() appointmentType: AppointmentType;

  formGroup: FormGroup;

  public selectedUserResource: UserResource;
  public usersResourceListData: ListData<UserResource>;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    public activeModal: NgbActiveModal,
    public usersResourcesService: UsersResourcesService
  ) {
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    switch (this.appointmentType) {
      case AppointmentType.APPOINTMENT_AUTHORIZED:
        this.loadUserResourcesAuthorized();
        break;
      case AppointmentType.APPOINTMENT_SYSTEM_ADMIN:
        this.loadUserResourcesSystemAdmin();
        break;
    }
    this.formGroup = new FormGroup({
      resource: new FormControl(this.selectedUserResource ? this.selectedUserResource : null, [Validators.required])
    });
  }

  loadUserResourcesAuthorized() {
    forkJoin({
      data: this.usersResourcesService.getUsersResourcesAuthorized(),
      cols: this.usersResourcesService.findUserColumns()
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe((response: { data, cols }) => {
      if (response?.data && response.cols) {
        this.selectedUserResource = response.data?.find(r => r.id === this.selectedUserResourceId);
        this.usersResourceListData = {
          data: response.data,
          colEnabled: [
            {
              column: UserResourceField.name,
              uiAvailable: true,
              uiUserShow: true,
              label: response.cols?.find(col => col.column === UserResourceField.name)?.label,
              type: ColumnType.TEXT,
              operations: response.cols?.find(col => col.column === UserResourceField.name)?.operations
            },
            {
              column: UserResourceField.surname,
              uiAvailable: true,
              uiUserShow: true,
              label: response.cols?.find(col => col.column === UserResourceField.surname)?.label,
              type: ColumnType.TEXT,
              operations: response.cols?.find(col => col.column === UserResourceField.surname)?.operations
            },
            {
              column: UserResourceField.email,
              uiAvailable: true,
              uiUserShow: true,
              label: response.cols?.find(col => col.column === UserResourceField.email)?.label,
              type: ColumnType.TEXT,
              operations: response.cols?.find(col => col.column === UserResourceField.email)?.operations
            },
            {
              column: UserResourceField.createdAt,
              uiAvailable: true,
              uiUserShow: true,
              label: response.cols?.find(col => col.column === UserResourceField.createdAt)?.label,
              type: ColumnType.DATE,
              operations: response.cols?.find(col => col.column === UserResourceField.surname)?.operations
            }
          ]
        };
      }
    });
  }

  loadUserResourcesSystemAdmin() {
    forkJoin({
      data: this.usersResourcesService.getUsersResourcesSystemAdmin(),
      cols: this.usersResourcesService.findUserColumns()
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe((response: { data, cols }) => {
      if (response?.data && response.cols) {
        this.selectedUserResource = response.data?.find(r => r.id === this.selectedUserResourceId);
        this.usersResourceListData = {
          data: response.data,
          colEnabled: [
            {
              column: UserResourceField.name,
              uiAvailable: true,
              uiUserShow: true,
              label: response.cols?.find(col => col.column === UserResourceField.name)?.label,
              type: ColumnType.TEXT,
              operations: response.cols?.find(col => col.column === UserResourceField.name)?.operations
            },
            {
              column: UserResourceField.surname,
              uiAvailable: true,
              uiUserShow: true,
              label: response.cols?.find(col => col.column === UserResourceField.surname)?.label,
              type: ColumnType.TEXT,
              operations: response.cols?.find(col => col.column === UserResourceField.surname)?.operations
            },
            {
              column: UserResourceField.email,
              uiAvailable: true,
              uiUserShow: true,
              label: response.cols?.find(col => col.column === UserResourceField.email)?.label,
              type: ColumnType.TEXT,
              operations: response.cols?.find(col => col.column === UserResourceField.email)?.operations
            },
            {
              column: UserResourceField.createdAt,
              uiAvailable: true,
              uiUserShow: true,
              label: response.cols?.find(col => col.column === UserResourceField.createdAt)?.label,
              type: ColumnType.DATE,
              operations: response.cols?.find(col => col.column === UserResourceField.createdAt)?.operations
            }
          ]
        };
      }
    });
  }

  confirm() {
    const form = this.formGroup.getRawValue();
    if (form) {
      this.activeModal.close(form?.resource);
    } else {
      this.activeModal.close(false);
    }
  }

  onSelectResource(selectedUserResource: UserResource) {
    this.formGroup.controls.resource.setValue(selectedUserResource);
  }
}
